import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

async function fetchImageAsUint8Array(url: string): Promise<Uint8Array> {
  const res = await fetch(url);
  const buffer = await res.arrayBuffer();
  return new Uint8Array(buffer);
}

// Helper to remove characters not supported by Helvetica (WinAnsiEncoding)
function sanitize(text: string): string {
  return text
    .replace(/ğ/g, "g")
    .replace(/Ğ/g, "G")
    .replace(/ş/g, "s")
    .replace(/Ş/g, "S")
    .replace(/ı/g, "i")
    .replace(/İ/g, "I")
    .replace(/ç/g, "c")
    .replace(/Ç/g, "C")
    .replace(/ö/g, "o")
    .replace(/Ö/g, "O")
    .replace(/ü/g, "u")
    .replace(/Ü/g, "U");
}

export async function generateInvoicePdf(
  participant: any,
  invoiceNumber: string,
  totalAmount: string,
  ticketType: string,
  quantity: number,
  unitPrice: number,
  discountAmount: number
): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage([595, 842]);
  const { width, height } = page.getSize();

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const primary = rgb(12 / 255, 89 / 255, 68 / 255);
  const gray = rgb(0.95, 0.95, 0.95);
  const labelColor = rgb(0.5, 0.5, 0.5);
  const red = rgb(1, 0, 0);

  const leftLogo = await pdfDoc.embedPng(await fetchImageAsUint8Array("/ica.png"));
  const rightLogo = await pdfDoc.embedPng(await fetchImageAsUint8Array("/logo.png"));

  let y = height - 60;

  page.drawImage(leftLogo, { x: 40, y, width: 140, height: 40 });
  page.drawImage(rightLogo, { x: width - 160, y, width: 110, height: 50 });

  y -= 60;

  const drawLeft = (label: string, value: string, offsetY: number) => {
    const line = `${label} ${sanitize(value)}`;
    page.drawText(line, { x: 40, y: y - offsetY, size: 9, font });
  };

  drawLeft("Invoice No.:", invoiceNumber, 0);
  drawLeft("Date:", new Date().toLocaleDateString(), 16);
  drawLeft("Event:", "48th ICA Conference & Exhibition", 32);
  drawLeft("Location:", "Dubai, UAE", 48);
  drawLeft("Dates:", "13th - 15th November, 2025", 64);

  const rightX = width - 40;
  const drawRight = (label: string, value: string, offsetY: number) => {
    const sanitizedValue = sanitize(value);
    const labelWidth = bold.widthOfTextAtSize(label, 9);
    const valueWidth = font.widthOfTextAtSize(sanitizedValue, 9);
    page.drawText(label, {
      x: rightX - valueWidth - labelWidth - 10,
      y: y - offsetY,
      size: 9,
      font: bold,
      color: labelColor,
    });
    page.drawText(sanitizedValue, {
      x: rightX - valueWidth,
      y: y - offsetY,
      size: 9,
      font,
    });
  };

  drawRight("Name:", participant.fullName || "N/A", 0);
  drawRight("Email:", participant.email || "N/A", 16);
  drawRight("Phone No.:", participant.mobile || "N/A", 32);
  drawRight("Company:", participant.company || "N/A", 48);
  drawRight("Position:", participant.position || "N/A", 64);

  y -= 140;

  // Table Header
  page.drawRectangle({ x: 40, y, width: width - 80, height: 20, color: primary });
  page.drawText("Ticket", { x: 50, y: y + 5, size: 10, font: bold, color: rgb(1, 1, 1) });
  page.drawText("Price", { x: width - 100, y: y + 5, size: 10, font: bold, color: rgb(1, 1, 1) });

  // Ticket Row
  y -= 20;
  page.drawText(sanitize(ticketType), { x: 50, y: y + 5, size: 10, font });
  const unitPriceText = `$${unitPrice.toFixed(2)}`;
  page.drawText(unitPriceText, {
    x: width - 50 - font.widthOfTextAtSize(unitPriceText, 10),
    y: y + 5,
    size: 10,
    font,
  });

  // Discount
  y -= 25;
  page.drawText("Discount", { x: 50, y: y + 5, size: 10, font });
  const discountText = `- $${discountAmount.toFixed(2)}`;
  page.drawText(discountText, {
    x: width - 50 - font.widthOfTextAtSize(discountText, 10),
    y: y + 5,
    size: 10,
    font,
  });

  // Total
  y -= 25;
  page.drawText("Total Amount", { x: 50, y: y + 5, size: 10, font: bold });
  const totalText = `$${totalAmount}`;
  page.drawText(totalText, {
    x: width - 50 - bold.widthOfTextAtSize(totalText, 10),
    y: y + 5,
    size: 10,
    font: bold,
  });

  // Bank Instructions
  y -= 50;
  page.drawRectangle({ x: 35, y: y - 110, width: width - 70, height: 110, color: gray });

  y -= 15;
  page.drawText("Please make the deposit to the following account:", {
    x: 45,
    y,
    size: 9,
    font: bold,
  });

  const lines = [
    "Bank Name: EMIRATES NBD",
    "Account Name: U A E FINANCIAL MARKETS ASSOCIATION",
    "Account Number: USD 1024464716202",
    "Swift Code: EBILAEAD",
    "IBAN: AE510260001024464716202",
    `Reference: Invoice #${invoiceNumber}`,
  ];
  y -= 14;

  for (const line of lines) {
    page.drawText(line, { x: 45, y, size: 9, font });
    y -= 14;
  }

  // NOTE
  y -= 30;
  const darkRed = rgb(160 / 255, 0, 0);
  page.drawText("NOTE", {
    x: width / 2 - bold.widthOfTextAtSize("NOTE", 10) / 2,
    y,
    size: 10,
    font: bold,
    color: darkRed,
  });

  y -= 15;
  const before = "Once the deposit is made, ";
  const boldPart = "please email the deposit slip";
  const after = " to eman@uaefma.com for confirmation.";

  const beforeWidth = font.widthOfTextAtSize(before, 9);
  const boldWidth = bold.widthOfTextAtSize(boldPart, 9);
  const afterWidth = font.widthOfTextAtSize(after, 9);
  let x = (width - (beforeWidth + boldWidth + afterWidth)) / 2;

  page.drawText(before, { x, y, size: 9, font, color: darkRed });
  x += beforeWidth;
  page.drawText(boldPart, { x, y, size: 9, font: bold, color: darkRed });
  x += boldWidth;
  page.drawText(after, { x, y, size: 9, font, color: darkRed });

  // Footer
  const footerY = 40;
  const lineY = footerY + 30;

  page.drawLine({
    start: { x: 40, y: lineY },
    end: { x: width - 40, y: lineY },
    thickness: 0.5,
    color: rgb(1, 140 / 255, 0),
  });

  const footerColor = rgb(12 / 255, 89 / 255, 68 / 255);
  const colWidth = (width - 80) / 4;
  const colX = [40, 40 + colWidth, 40 + colWidth * 2, 40 + colWidth * 3];

  page.drawText("513, Fairmont Dubai, Sheikh", {
    x: colX[0] + (colWidth - font.widthOfTextAtSize("513, Fairmont Dubai, Sheikh", 9)) / 2,
    y: footerY + 12,
    size: 9,
    font,
    color: footerColor,
  });
  page.drawText("Zayed Road, Dubai, UAE", {
    x: colX[0] + (colWidth - font.widthOfTextAtSize("Zayed Road, Dubai, UAE", 9)) / 2,
    y: footerY,
    size: 9,
    font,
    color: footerColor,
  });

  page.drawText("eman@uaefma.com", {
    x: colX[1] + (colWidth - font.widthOfTextAtSize("eman@uaefma.com", 9)) / 2,
    y: footerY,
    size: 9,
    font,
    color: footerColor,
  });

  page.drawText("+971 4 256 6622", {
    x: colX[2] + (colWidth - font.widthOfTextAtSize("+971 4 256 6622", 9)) / 2,
    y: footerY,
    size: 9,
    font,
    color: footerColor,
  });

  page.drawText("www.icaconferencedubai.com", {
    x: colX[3] + (colWidth - font.widthOfTextAtSize("www.icaconferencedubai.com", 9)) / 2,
    y: footerY + 12,
    size: 9,
    font,
    color: footerColor,
  });
  page.drawText("www.uaefma.com", {
    x: colX[3] + (colWidth - font.widthOfTextAtSize("www.uaefma.com", 9)) / 2,
    y: footerY,
    size: 9,
    font,
    color: footerColor,
  });

  return await pdfDoc.save();
}
